import React from "react";

/**
 * SectionBlock — Common component matching existing FeatureSection pattern
 *
 * Props:
 * @param {string}           image          - Image src
 * @param {string|ReactNode} label          - Section heading
 * @param {string|ReactNode} description    - Body text or JSX
 * @param {boolean}          imageLeft      - true = image left, false = image right (default: true)
 * @param {string}           sectionBg      - Background color for both halves (default: "#f8f8f8")
 * @param {string}           textAlign      - "left" or "right" (default: "left")
 * @param {string}           bodyColor      - Paragraph color (default: "#3b3b3b")
 * @param {string}           imageMaxWidth  - Max width of image (default: "306px")
 * @param {boolean}          framedImage    - Wrap image in bordered card (default: false)
 * @param {boolean}          htmlDescription- Render description as HTML (default: false)
 * @param {ReactNode}        textContent    - Fully custom JSX for text half (overrides label/description)
 * @param {string}           imageBg        - Background for image half (defaults to sectionBg)
 * @param {string}           textBg         - Background for text half (defaults to sectionBg)
 * @param {string}           sectionHeight  - Desktop section height (default: "540px")
 */

const SectionBlock = ({
    image,
    label,
    description,
    textContent = null,
    imageLeft = true,
    sectionBg = "#f8f8f8",
    imageBg,
    textBg,
    sectionHeight = "540px",
    textAlign = "left",
    bodyColor = "#3b3b3b",
    imageMaxWidth = "306px",
    framedImage = false,
    htmlDescription = false,
}) => (
    <div
        className="bg-white w-full flex flex-col lg:flex-row"
        style={{ minHeight: sectionHeight }}
    >
        {/* Image Half */}
        <div
            className={`w-full lg:w-1/2 flex items-center justify-center py-5 lg:py-0 min-h-[320px] lg:min-h-full order-1 ${imageLeft ? "lg:order-1" : "lg:order-2"
                }`}
            style={{ backgroundColor: imageBg ?? sectionBg }}
        >
            {framedImage ? (
                <div
                    className="border border-[#bfd1ff] rounded-[9px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.08)] overflow-hidden w-full mx-6 lg:mx-0"
                    style={{ maxWidth: imageMaxWidth }}
                >
                    <img
                        src={image}
                        alt={typeof label === "string" ? label : "section image"}
                        className="w-full h-full object-cover"
                    />
                </div>
            ) : (
                <img
                    src={image}
                    alt={typeof label === "string" ? label : "section image"}
                    className="block h-auto shrink-0 object-contain px-6 lg:px-0 max-w-full"
                    style={{ width: imageMaxWidth }}
                />
            )}
        </div>

        {/* Text Half */}
        <div
            className={`w-full lg:w-1/2 flex items-center justify-center min-h-[320px] lg:min-h-0 px-6 lg:px-[65px] order-2 ${imageLeft ? "lg:order-2" : "lg:order-1"
                }`}
            style={{ backgroundColor: textBg ?? sectionBg }}
        >
            {textContent ? (
                textContent
            ) : (
                <div
                    className={`w-full max-w-[590px] flex flex-col gap-5 ${textAlign === "right" ? "text-right items-end" : "text-left items-start"
                        }`}
                >
                    {label && (
                        <h3 className="text-[#171717] text-2xl font-medium font-['Inter'] leading-normal w-full">
                            {label}
                        </h3>
                    )}
                    {htmlDescription ? (
                        <div
                            className="text-base font-normal font-['Poppins'] leading-[26px] w-full [&_strong]:font-medium"
                            style={{ color: bodyColor }}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    ) : (
                        <div
                            className="text-base font-normal font-['Poppins'] leading-[26px] w-full"
                            style={{ color: bodyColor }}
                        >
                            {description}
                        </div>
                    )}
                </div>
            )}
        </div>
    </div>
);

export default SectionBlock;